import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';

const AddEditEvent = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(eventId);

  const [formData, setFormData] = useState({
    title: '',
    title_en: '',
    date: '',
    location: '',
    type: 'Gala',
    description: '',
    description_en: '',
    image: '',
    price_member: 0,
    price_non_member: 0,
    total_seats: 0,
    seats_sold: 0,
    status: 'Disponible',
    active: true,
    detail_link: ''
  });

  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);

  const eventTypes = ['Gala', 'Festival', 'Fashion', 'Soirée', 'Conférence', 'Autre'];
  const statusOptions = ['Disponible', 'Complet', 'Annulé', 'Terminé'];

  useEffect(() => {
    if (isEdit) {
      fetchEvent();
    }
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${eventId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const event = await response.json();
        setFormData({
          title: event.title || '',
          title_en: event.title_en || '',
          date: event.date || '',
          location: event.location || '',
          type: event.type || 'Gala',
          description: event.description || '',
          description_en: event.description_en || '',
          image: event.image || '',
          price_member: event.price_member || 0,
          price_non_member: event.price_non_member || 0,
          total_seats: event.total_seats || 0,
          seats_sold: event.seats_sold || 0,
          status: event.status || 'Disponible',
          active: event.active !== false,
          detail_link: event.detail_link || ''
        });
        if (event.image) {
          if (event.image.startsWith('http')) {
            setImagePreview(event.image);
          } else {
            const previewUrl = event.image.replace('/uploads/', '/api/uploads/');
            setImagePreview(`${process.env.REACT_APP_BACKEND_URL}${previewUrl}`);
          }
        }
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("L'image est trop volumineuse (max 5MB)");
      return;
    }

    setUploadingImage(true);

    try {
      const token = localStorage.getItem('adminToken');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/events/upload-image`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataUpload
        }
      );

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, image: data.url }));
        const previewUrl = data.url.replace('/uploads/', '/api/uploads/');
        setImagePreview(`${process.env.REACT_APP_BACKEND_URL}${previewUrl}`);
        alert('Image uploadée avec succès !');
      } else {
        alert("Erreur lors de l'upload de l'image");
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert("Erreur lors de l'upload de l'image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const url = isEdit
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${eventId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/events`;

      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert(isEdit ? 'Événement mis à jour avec succès' : 'Événement créé avec succès');
        navigate('/admin/events');
      } else {
        const error = await response.json();
        alert('Erreur: ' + (error.detail || 'Erreur inconnue'));
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <AdminLayout>
        <div style={{ padding: '32px' }}>
          <p>Chargement...</p>
        </div>
      </AdminLayout>
    );
  }

  const getTypeColor = (type) => {
    const colors = {
      'Gala': '#8b4f6d',
      'Festival': '#2c5f7d',
      'Fashion': '#8b7355'
    };
    return colors[type] || '#666666';
  };

  return (
    <AdminLayout>
      <div style={{ padding: '32px' }}>
        <button
          onClick={() => navigate('/admin/events')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '24px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-secondary)'
          }}
        >
          <ArrowLeft size={20} />
          <span className="body-regular">Retour</span>
        </button>

        <h1 className="hero-medium" style={{ marginBottom: '32px' }}>
          {isEdit ? "Modifier l'événement" : 'Ajouter un événement'}
        </h1>

        <form onSubmit={handleSubmit} style={{
          maxWidth: '900px',
          background: 'var(--bg-primary)',
          border: '1px solid var(--border-light)',
          padding: '32px'
        }}>
          {/* Informations de base */}
          <h3 className="heading-3" style={{ marginBottom: '24px', color: '#8b4f6d' }}>
            🎉 Informations de base
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Titre (FR) *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="ex: Soirée de Gala H&G : Élégance Nordique"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Titre (EN)
              </label>
              <input
                type="text"
                name="title_en"
                value={formData.title_en}
                onChange={handleChange}
                placeholder="ex: H&G Gala Evening: Nordic Elegance"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Type *
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  background: 'var(--bg-primary)'
                }}
              >
                {eventTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Date *
              </label>
              <input
                type="text"
                name="date"
                value={formData.date}
                onChange={handleChange}
                required
                placeholder="ex: 5 Mars 2026"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Lieu *
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
                placeholder="ex: Paris, France"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Description (FR) *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={4}
                placeholder="Description détaillée de l'événement..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Description (EN)
              </label>
              <textarea
                name="description_en"
                value={formData.description_en}
                onChange={handleChange}
                rows={4}
                placeholder="Detailed description of the event..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>

          {/* Image */}
          <div style={{ marginTop: '32px', paddingTop: '32px', borderTop: '1px solid var(--border-light)' }}>
            <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
              🖼️ Image
            </label>
            <p className="body-small" style={{ marginBottom: '12px', color: 'var(--text-secondary)' }}>
              📐 Dimensions recommandées : 800 x 400 px • Format : JPG ou PNG • Poids : &lt; 500 KB
            </p>

            {imagePreview && (
              <div style={{ marginBottom: '12px' }}>
                <img
                  src={imagePreview}
                  alt="Aperçu"
                  style={{
                    width: '100%',
                    maxWidth: '400px',
                    height: 'auto',
                    border: '1px solid var(--border-light)'
                  }}
                />
              </div>
            )}

            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={uploadingImage}
              style={{
                padding: '12px',
                border: '1px solid var(--border-light)',
                fontSize: '14px',
                fontFamily: 'inherit',
                cursor: uploadingImage ? 'not-allowed' : 'pointer',
                width: '100%'
              }}
            />
            {uploadingImage && (
              <p className="body-small" style={{ marginTop: '8px', color: '#2c5f7d' }}>
                Upload en cours...
              </p>
            )}

            <div style={{ marginTop: '12px' }}>
              <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                Ou URL externe de l'image
              </label>
              <input
                type="text"
                name="image"
                value={formData.image}
                onChange={(e) => {
                  handleChange(e);
                  setImagePreview(e.target.value);
                }}
                placeholder="https://..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  fontSize: '14px',
                  fontFamily: 'inherit'
                }}
              />
            </div>
          </div>

          {/* Tarification */}
          <div style={{ marginTop: '32px', paddingTop: '32px', borderTop: '2px solid #8b4f6d' }}>
            <h3 className="heading-3" style={{ marginBottom: '24px', color: '#8b4f6d' }}>
              💰 Tarification
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Prix Membre (€) *
                </label>
                <input
                  type="number"
                  name="price_member"
                  value={formData.price_member}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Prix Non-membre (€) *
                </label>
                <input
                  type="number"
                  name="price_non_member"
                  value={formData.price_non_member}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Gestion des places */}
          <div style={{ marginTop: '32px', paddingTop: '32px', borderTop: '1px solid var(--border-light)' }}>
            <h3 className="heading-3" style={{ marginBottom: '24px', color: '#2c5f7d' }}>
              👥 Gestion des places
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }}>
              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Places totales *
                </label>
                <input
                  type="number"
                  name="total_seats"
                  value={formData.total_seats}
                  onChange={handleChange}
                  required
                  min="0"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Places vendues *
                </label>
                <input
                  type="number"
                  name="seats_sold"
                  value={formData.seats_sold}
                  onChange={handleChange}
                  required
                  min="0"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div>
                <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  Statut *
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    background: 'var(--bg-primary)'
                  }}
                >
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginTop: '16px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleChange}
                  style={{ cursor: 'pointer' }}
                />
                <span className="body-regular">Événement actif (visible sur le site)</span>
              </label>
            </div>
          </div>

          {/* Lien de détail */}
          <div style={{ marginTop: '32px', paddingTop: '32px', borderTop: '1px solid var(--border-light)' }}>
            <label className="body-regular" style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
              🔗 Lien vers la page de détail (optionnel)
            </label>
            <input
              type="text"
              name="detail_link"
              value={formData.detail_link}
              onChange={handleChange}
              placeholder="ex: /member/elegance-nordique"
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid var(--border-light)',
                fontSize: '14px',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Boutons */}
          <div style={{
            marginTop: '32px',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={() => navigate('/admin/events')}
              style={{
                padding: '12px 24px',
                background: 'transparent',
                border: '1px solid var(--border-light)',
                cursor: 'pointer',
                color: 'var(--text-secondary)'
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: loading ? 0.6 : 1
              }}
            >
              <Save size={20} />
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AddEditEvent;
